/**
 * Visão de dados wearable no atendimento (lado esquerdo).
 * Layout alinhado ao FitnessDataModal do paciente: faixa de data com setas + calendário,
 * chips de horário e cards em grade com ícone, título e valor.
 */
import {
  alpha,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import dayjs, { type Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";

import { FitnessModalDateRow } from "@/pages/home/components/PatientDash/compontents/FitnessModalDateRow";
import { useMedicalCareContext } from "@/contexts/MedicalCareContext";
import { oids } from "@/configs/Settings";
import { WEARABLE_LOINC_LIST } from "@/services/fhir/observation/wearableObservationBuilder";
import { useFhirData } from "@/utils/useFhirData";
import { formatWearableObservationValue } from "@/utils/wearableObservationFormat";

import { scrollbarStyle } from "../scrollbarStyle";

import {
  getPrimaryLoincCode,
  getWearableMedicalCardPresentation,
} from "./wearableMedicalCardPresentation";

/**
 * Códigos LOINC aceitos na visão do atendimento.
 * Inclui todos os do envio Google Health (WEARABLE_LOINC_LIST) e extras (água, sono, SpO2, peso, pele).
 */
const EXTRA_WEARABLE_LOINC_CODES = [
  "8999-5",
  "93828-2",
  "93829-0",
  "93830-8",
  "93831-6",
  "59408-5",
  "96634-8",
  "29463-7",
  "81264-4",
] as const;

const WEARABLE_CODES = [
  ...WEARABLE_LOINC_LIST.map((d) => d.loinc),
  ...EXTRA_WEARABLE_LOINC_CODES,
];

function normalizeObservationLabel(label: string): string {
  if (label === "SpO2 Tempo abaixo de 90%") return "SpO2 abaixo de 90%";
  if (label === "Calorias (Consumidas)") return "Calorias";
  return label;
}

/** Card no estilo FitnessDataModal: gradiente neutro, ícone circular, título e valor. */
function WearableMetricCard({
  entry,
  theme,
}: {
  entry: any;
  theme: Theme;
}) {
  const resource = entry?.resource;
  const base = getWearableMedicalCardPresentation(resource, theme);
  const label = normalizeObservationLabel(base.label);
  const IconCmp = base.icon;
  const color = base.color;
  const value = formatWearableObservationValue(resource);

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "neutral200.main",
        minHeight: 120,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        background: (t) =>
          `linear-gradient(145deg, ${t.palette.neutral0.main} 0%, ${t.palette.neutral100.main} 100%)`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="subtitle2" sx={{ pr: 1 }}>
          {label}
        </Typography>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "50%",
            bgcolor: alpha(color, 0.12),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <IconCmp sx={{ color, fontSize: 18 }} />
        </Box>
      </Box>
      <Typography
        variant="h6"
        color="text.primary"
        sx={{
          mt: 1,
          fontWeight: 700,
          fontSize: { xs: "1rem", sm: "1.25rem" },
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export function WearableDataView() {
  const theme = useTheme();
  const { summaryData, loadingSummary, patient } = useMedicalCareContext();
  const today = dayjs().startOf("day");
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [selectedIssuedTime, setSelectedIssuedTime] = useState<string | "all">(
    "all"
  );
  const [sonoFirst, setSonoFirst] = useState(true);

  const observationSummary = useFhirData.extractPatientSummaryResource(
    summaryData,
    "Observation"
  )?.[0];

  const patientIdentifier =
    patient?.identifier?.find((id: any) => id.system?.includes(oids.cpf))?.value ||
    patient?.id;
  const patientPerformerReference = patientIdentifier
    ? `Patient/${oids.cpf}-${patientIdentifier}`
    : "";

  const observationEntries = useMemo(() => {
    const entries = observationSummary?.entry ?? [];
    return entries.filter((e: any) => {
      // Dados wearable não dependem do questionário clínico nem do contrato de questionários
      // (isso vale só para a aba Questionários em Dados objetivos).
      const code = e?.resource?.code?.coding?.[0]?.code;
      const isWearable = code && WEARABLE_CODES.includes(code);
      const performerRef = e?.resource?.performer?.[0]?.reference;
      const isFromPatient =
        !patientPerformerReference || performerRef === patientPerformerReference;
      return isWearable && isFromPatient;
    });
  }, [observationSummary?.entry, patientPerformerReference]);

  const filteredObservations = useMemo(() => {
    if (!selectedDate || !dayjs(selectedDate).isValid()) return [];
    const selectedDateKey = dayjs(selectedDate).format("YYYY-MM-DD");
    return observationEntries
      .filter((entry: any) => {
        const r = entry?.resource;
        const effective =
          r?.effectiveDateTime ?? r?.effectivePeriod?.start ?? r?.effectivePeriod?.end;
        if (!effective) return false;
        return dayjs(effective).format("YYYY-MM-DD") === selectedDateKey;
      })
      .sort((a: any, b: any) => {
        const getSortDate = (entry: any) => {
          const r = entry?.resource;
          if (r?.issued) return dayjs(r.issued).valueOf();
          if (r?.effectiveDateTime) return dayjs(r.effectiveDateTime).valueOf();
          if (r?.effectivePeriod?.start) return dayjs(r.effectivePeriod.start).valueOf();
          return dayjs(r?.meta?.lastUpdated).valueOf() || 0;
        };
        return getSortDate(b) - getSortDate(a);
      });
  }, [observationEntries, selectedDate]);

  const issuedGroups = useMemo(() => {
    const groups: Record<string, any[]> = {};
    filteredObservations.forEach((entry: any) => {
      const issued = entry?.resource?.issued;
      if (!issued) return;
      const timeKey = dayjs(issued).format("HH:mm");
      if (!groups[timeKey]) groups[timeKey] = [];
      groups[timeKey].push(entry);
    });
    return Object.entries(groups)
      .map(([time, items]) => ({ time, items }))
      .sort((a, b) => dayjs(b.time, "HH:mm").diff(dayjs(a.time, "HH:mm")));
  }, [filteredObservations]);

  useEffect(() => {
    if (issuedGroups.length > 0) setSelectedIssuedTime(issuedGroups[0].time);
    else setSelectedIssuedTime("all");
  }, [issuedGroups]);

  const observationsForSelectedTime = useMemo(() => {
    if (selectedIssuedTime === "all") return filteredObservations;
    const group = issuedGroups.find((g) => g.time === selectedIssuedTime);
    return group?.items ?? [];
  }, [filteredObservations, issuedGroups, selectedIssuedTime]);

  const handleChangeDay = (deltaDays: number) => {
    if (loadingSummary) return;
    const candidateDate = selectedDate.add(deltaDays, "day");
    const newDate = candidateDate.isAfter(today, "day") ? today : candidateDate;
    setSelectedDate(newDate);
  };

  const handlePickDate = (picked: Dayjs) => {
    if (loadingSummary) return;
    const clamped = picked.isAfter(today, "day") ? today : picked.startOf("day");
    setSelectedDate(clamped);
  };

  if (loadingSummary) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 200 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        /* Padding um pouco menor à esquerda e maior à direita: evita sensação de “vazio” à esquerda
           e compensa o alinhamento com a área rolável dos cards. */
        pt: 1.5,
        pr: 1.25,
        pb: 1.5,
        pl: 1,
        flex: 1,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
      }}
      spacing={1.25}
    >
      {/* Linha única: data → Sono primeiro → horários (com wrap em telas estreitas). */}
      <Stack
        direction="row"
        flexWrap="wrap"
        alignItems="center"
        sx={{
          gap: { xs: 1, sm: 1.5 },
          rowGap: 1,
          width: "100%",
          flexShrink: 0,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "fit-content",
            maxWidth: "100%",
            p: { xs: 1, sm: 1.25 },
            backgroundColor: "primary800.main",
            color: "white",
            borderRadius: 2,
            flexShrink: 0,
          }}
        >
          <FitnessModalDateRow
            compact
            inline
            fullWidth={false}
            selectedDate={selectedDate}
            today={today}
            loading={loadingSummary}
            onPrevDay={() => handleChangeDay(-1)}
            onNextDay={() => handleChangeDay(1)}
            onPickDate={handlePickDate}
          />
        </Paper>

        {filteredObservations.length > 0 && (
          <FormControlLabel
            sx={{
              m: 0,
              flexShrink: 0,
              alignItems: "center",
            }}
            control={
              <Switch
                size="small"
                checked={sonoFirst}
                onChange={(_, checked) => setSonoFirst(checked)}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Sono primeiro
              </Typography>
            }
          />
        )}

        {filteredObservations.length > 0 && issuedGroups.length > 1 && (
          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              flex: "1 1 auto",
              minWidth: 0,
              minHeight: 36,
              overflowX: "auto",
              py: 0.25,
              ...scrollbarStyle,
            }}
          >
            {issuedGroups.map((group) => (
              <Button
                key={group.time}
                size="small"
                variant={
                  selectedIssuedTime === group.time ? "contained" : "outlined"
                }
                color="primary"
                onClick={() => setSelectedIssuedTime(group.time)}
                sx={{ flexShrink: 0 }}
              >
                {group.time}
              </Button>
            ))}
          </Stack>
        )}
      </Stack>

      {filteredObservations.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nenhum dado encontrado para o dia selecionado.
        </Typography>
      ) : (
        <Stack
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflowY: "auto",
            overflowX: "hidden",
            gap: 2,
            pb: 2,
            WebkitOverflowScrolling: "touch",
            ...scrollbarStyle,
          }}
        >
          {(() => {
            const general: any[] = [];
            const sleepAndSpO2: any[] = [];
            observationsForSelectedTime.forEach((entry: any) => {
              const resource = entry?.resource;
              const code = resource?.code?.coding?.[0]?.code;
              const text =
                resource?.code?.text || resource?.code?.coding?.[0]?.display || "";
              const lower = text.toLowerCase();
              const isSleepOrSpO2 = lower.startsWith("sono") || lower.startsWith("spo2");
              if (isSleepOrSpO2 || code === "81264-4") sleepAndSpO2.push(entry);
              else general.push(entry);
            });
            sleepAndSpO2.sort((a: any, b: any) => {
              if (a?.resource?.code?.coding?.[0]?.code === "81264-4") return 1;
              if (b?.resource?.code?.coding?.[0]?.code === "81264-4") return -1;
              return 0;
            });

            const renderSection = (title: string, items: any[]): ReactNode => {
              if (!items.length) return null;
              return (
                <Stack
                  key={title}
                  spacing={1.5}
                  sx={title === "Dados objetivos gerais" ? { pb: 2 } : undefined}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      px: 0.5,
                      py: 0.75,
                      borderRadius: 1.5,
                      backgroundColor:
                        title === "Dados objetivos de sono"
                          ? "primary100.main"
                          : "neutral200.main",
                      border: "1px solid",
                      borderColor:
                        title === "Dados objetivos de sono"
                          ? "primary100.main"
                          : "neutral200.main",
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 6,
                          height: 20,
                          borderRadius: 2,
                          bgcolor:
                            title === "Dados objetivos de sono"
                              ? "primary.main"
                              : "neutral500.main",
                        }}
                      />
                      <Typography variant="subtitle1" fontWeight={600} color="neutral900.main">
                        {title}
                      </Typography>
                    </Stack>
                  </Box>
                  {/* CSS Grid com gap: evita margens negativas do Mui Grid (cortavam o último card à direita). */}
                  <Box
                    sx={{
                      display: "grid",
                      width: "100%",
                      boxSizing: "border-box",
                      gap: 1.5,
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        md: "repeat(4, minmax(0, 1fr))",
                      },
                    }}
                  >
                    {items.map((entry: any, idx: number) => (
                      <Box
                        key={
                          entry?.resource?.id ??
                          `${getPrimaryLoincCode(entry?.resource) ?? "obs"}-${idx}`
                        }
                        sx={{ minWidth: 0 }}
                      >
                        <WearableMetricCard entry={entry} theme={theme} />
                      </Box>
                    ))}
                  </Box>
                </Stack>
              );
            };

            return sonoFirst ? (
              <>
                {renderSection("Dados objetivos de sono", sleepAndSpO2)}
                {renderSection("Dados objetivos gerais", general)}
              </>
            ) : (
              <>
                {renderSection("Dados objetivos gerais", general)}
                {renderSection("Dados objetivos de sono", sleepAndSpO2)}
              </>
            );
          })()}
        </Stack>
      )}
    </Stack>
  );
}
